import type { ComponentType, HTMLAttributes, ReactNode, SVGProps } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatPercent } from '../../lib/format';
import { Card } from '../Card';
import { Skeleton } from '../Skeleton';
import { Tooltip } from '../Tooltip';
import { MetricValue } from './MetricValue';
import type { MetricFormat } from './MetricValue';

/**
 * A MÉTRICA declara sua própria polaridade. Nunca "verde = subiu":
 * "saídas subiram 12%" tem que ficar VERMELHO mesmo subindo (seção 7).
 *
 *  higher-is-better — entradas, conversão, saldo
 *  lower-is-better  — saídas, evasão, custo por entrada
 *  neutral          — variação sem julgamento (ex.: nº de grupos)
 */
export type MetricPolarity = 'higher-is-better' | 'lower-is-better' | 'neutral';

export interface StatTileDelta {
  /** Fração da variação: `0.124` = 12,4%. `null` = sem base de comparação. */
  value: number | null;
  polarity: MetricPolarity;
  /** Contra o quê (ex.: "vs. 7 dias anteriores"). */
  comparedTo?: string;
}

export interface StatTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  label: string;
  /** `null` = sem dados (`—`); `0` = zero medido. */
  value: number | null | undefined;
  format?: MetricFormat;
  decimals?: number;
  abbreviate?: 'auto' | 'never';
  currency?: string;
  suffix?: string;
  delta?: StatTileDelta;
  /** Sparkline, ConfidenceBadge inline, o que for. Vai no rodapé do tile. */
  footer?: ReactNode;
  /** Marca de confiança colada no valor (ConfidenceBadge variant="inline"). */
  annotation?: ReactNode;
  /** `compact` para grid de 6+ tiles; `default` para grid de 3-4. */
  size?: 'compact' | 'default';
  /**
   * Ícone no canto, dentro de um quadrado discreto (referência do dono, 19/09).
   *
   * É **decoração com função de âncora**: numa grade de cinco tiles iguais, a forma ajuda a
   * achar o certo de relance mais rápido que ler cinco rótulos. Por isso ele é `aria-hidden`
   * e nunca carrega informação que não esteja no texto — quem usa leitor de tela não perde
   * nada, e quem olha de longe acha antes.
   */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Micro-rótulo mono no canto ("HOJE", "NO GRÁFICO"). O `icon`, se houver, tem prioridade. */
  tag?: string;
  /** Linhas de detalhe embaixo do número (Ontem / Este mês / Mês passado). */
  breakdown?: ReadonlyArray<{ label: string; value: ReactNode }>;
  /**
   * `brand` = O KPI em destaque da tela: fundo violeta + brilho. UM por tela —
   * é a mesma vaga de "única cor de ação" do botão primário.
   */
  tone?: 'default' | 'brand';
  loading?: boolean;
  /** Explica por que não há dado. Só aparece quando `value` é nulo. */
  noDataReason?: string;
}

function deltaTone(value: number, polarity: MetricPolarity): string {
  if (value === 0 || polarity === 'neutral') return 'bg-muted text-muted-foreground';
  const good = polarity === 'higher-is-better' ? value > 0 : value < 0;
  return good ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger';
}

// No tile de marca a cor semântica sumiria contra o violeta: a pílula vira
// translúcida e o sentido fica na seta e no sinal escrito (os outros 2 canais).
const DELTA_ON_BRAND = 'bg-primary-foreground/20 text-primary-foreground';

/**
 * KPI. Anatomia fixa: label pequeno em cima, NÚMERO grande (o herói),
 * variação ao lado — nunca embaixo, nunca em badge separado (seção 6).
 * Sem sombra: tile de grid separa por borda (seção 5).
 */
export function StatTile({
  label,
  value,
  format = 'number',
  decimals,
  abbreviate = 'auto',
  currency,
  suffix,
  delta,
  footer,
  annotation,
  size = 'default',
  icon: Icone,
  tag,
  breakdown,
  tone = 'default',
  loading = false,
  noDataReason = 'Não houve medição neste período — não é o mesmo que zero.',
  className,
  ...props
}: StatTileProps) {
  const hasData = value !== null && value !== undefined && Number.isFinite(value);

  if (loading) {
    // skeleton no formato EXATO do tile final: mesma altura, mesma ordem
    return (
      <Card padded={false} className={cn('p-4', className)}>
        <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
          <span className="sr-only">Carregando {label}</span>
          <div className="flex items-start justify-between gap-2">
            <Skeleton className="h-3 w-24" />
            {/* o quadrado do ícone entra no esqueleto: sem ele a caixa "pula" ao carregar */}
            {Icone ? <Skeleton className="size-8 rounded-md" /> : null}
          </div>
          <Skeleton className={cn('mt-1', size === 'compact' ? 'h-5.5 w-20' : 'h-7 w-28')} />
          {breakdown ? (
            <div className="mt-3 flex flex-col gap-1.5">
              {breakdown.map((item) => (
                <Skeleton key={item.label} className="h-2.5 w-full" />
              ))}
            </div>
          ) : (
            <Skeleton className="h-3 w-16" />
          )}
        </div>
      </Card>
    );
  }

  const brand = tone === 'brand';

  const valueNode = (
    <MetricValue
      value={value}
      format={format}
      {...(decimals !== undefined ? { decimals } : {})}
      abbreviate={abbreviate}
      {...(currency ? { currency } : {})}
      {...(suffix ? { suffix } : {})}
      className={cn(
        'font-semibold',
        brand ? 'text-primary-foreground' : 'text-foreground',
        size === 'compact' ? 'text-xl' : 'text-2xl',
        !hasData && (brand ? 'text-primary-foreground/70' : 'text-subtle-foreground'),
      )}
    />
  );

  return (
    <Card
      padded={false}
      data-tone={tone}
      className={cn(
        'flex min-w-0 flex-col p-4',
        brand && 'border-transparent bg-primary text-primary-foreground shadow-brand',
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={cn(
            'min-w-0 truncate text-xs font-medium',
            brand ? 'text-primary-foreground/75' : 'text-muted-foreground',
          )}
        >
          {label}
        </p>
        {Icone ? (
          <span
            aria-hidden="true"
            className={cn(
              'grid size-8 shrink-0 place-items-center rounded-md',
              brand ? 'bg-primary-foreground/15 text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}
          >
            <Icone className="size-4" />
          </span>
        ) : tag ? (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap font-mono text-2xs font-medium uppercase tracking-caps',
              brand ? 'text-primary-foreground/60' : 'text-subtle-foreground',
            )}
          >
            {tag}
          </span>
        ) : null}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {hasData ? (
          valueNode
        ) : (
          // "sem dados" explica a si mesmo: o travessão sozinho vira dúvida
          <Tooltip content={noDataReason} side="bottom">
            <span
              tabIndex={0}
              className="cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {valueNode}
            </span>
          </Tooltip>
        )}

        {annotation ? <span className="shrink-0">{annotation}</span> : null}

        {delta ? <DeltaIndicator {...delta} inverse={brand} /> : null}
      </div>

      {breakdown && breakdown.length > 0 ? (
        <dl
          className={cn(
            'mt-4 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 border-t pt-3 text-2xs font-normal',
            brand ? 'border-primary-foreground/20' : 'border-border',
          )}
        >
          {breakdown.map((item) => (
            <div key={item.label} className="contents">
              <dt className={cn('truncate', brand ? 'text-primary-foreground/75' : 'text-subtle-foreground')}>
                {item.label}
              </dt>
              <dd className="nums text-right font-semibold">{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {footer ? <div className="mt-2.5">{footer}</div> : null}
    </Card>
  );
}

export interface DeltaIndicatorProps extends StatTileDelta {
  /** Sobre fundo de marca (StatTile `tone="brand"`): pílula translúcida. */
  inverse?: boolean;
  /** O porquê do "—" quando não há base (G2: "sem período anterior para comparar"). */
  noBaseReason?: string;
}

export function DeltaIndicator({
  value,
  polarity,
  comparedTo,
  inverse = false,
  noBaseReason = 'sem período anterior para comparar',
}: DeltaIndicatorProps) {
  if (value === null || !Number.isFinite(value)) {
    // "—" explica a si mesmo (G2): o tooltip diz por que não há variação.
    return (
      <Tooltip content={noBaseReason} side="bottom">
        <span
          tabIndex={0}
          className={cn(
            'nums inline-flex h-5 cursor-help items-center rounded-pill px-1.75 text-2xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            inverse ? DELTA_ON_BRAND : 'bg-muted text-muted-foreground',
          )}
        >
          <span aria-hidden="true">—</span>
          <span className="sr-only">{noBaseReason}</span>
        </span>
      </Tooltip>
    );
  }

  // TRÊS canais redundantes: cor + ícone (seta) + sinal escrito (+/-).
  // Cor sozinha não informa quem não distingue verde de vermelho (seção 1.3).
  const Icon = value > 0 ? ArrowUp : value < 0 ? ArrowDown : Minus;
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : '';
  const text = value === 0 ? '0%' : `${sign}${formatPercent(Math.abs(value), 1)}`;
  const direction = value > 0 ? 'aumento de' : value < 0 ? 'queda de' : 'estável em';

  return (
    <span
      className={cn(
        // pílula suave: o tom diz bom/ruim, a seta e o sinal dizem a direção
        'nums inline-flex h-5 items-center gap-0.5 rounded-pill px-1.75 text-2xs font-semibold',
        inverse ? DELTA_ON_BRAND : deltaTone(value, polarity),
      )}
      title={comparedTo}
    >
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span aria-hidden="true">{text}</span>
      <span className="sr-only">
        {direction} {formatPercent(Math.abs(value), 1)}
        {comparedTo ? ` ${comparedTo}` : ''}
      </span>
    </span>
  );
}
